/* CtaBlock — Animated gradient CTA block with inline editing */

import InlineTextEditor from '@/components/cms/InlineTextEditor'

interface CtaBlockProps {
  data: Record<string, string>
  onUpdateData?: (updates: Record<string, string>) => void
}

export default function CtaBlock({ data, onUpdateData }: CtaBlockProps) {
  const headline = data.headline || 'Ready to Get Started?'
  const body = data.body || 'Book your appointment today.'
  const buttonText = data.buttonText || 'Book Now'

  return (
    <div data-testid="card-cta-block" data-keep-white className="group relative overflow-hidden rounded-xl min-h-[180px] flex items-center justify-center">
      {/* Animated gradient background */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 30%, #581c87 60%, #9f1239 100%)',
          backgroundSize: '400% 400%',
          animation: 'gradientShift 8s ease infinite',
        }}
      />

      {/* Noise & grain overlay */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
      }} />

      {/* Glass inner card */}
      <div className="relative z-10 text-center px-8 py-10">
        {onUpdateData ? (
          <InlineTextEditor
            value={headline}
            onChange={(v) => onUpdateData({ headline: v })}
            variant="heading"
            placeholder="Ready to Get Started?"
            className="text-xl font-bold text-white mb-2 tracking-tight"
          />
        ) : (
          <h3 className="text-xl font-bold text-white mb-2 tracking-tight">
            {headline}
          </h3>
        )}

        {onUpdateData ? (
          <InlineTextEditor
            value={body}
            onChange={(v) => onUpdateData({ body: v })}
            variant="body"
            placeholder="Book your appointment today."
            className="text-sm text-white/50 mb-6"
          />
        ) : (
          <p className="text-sm text-white/50 mb-6">
            {body}
          </p>
        )}

        <button className="
          inline-flex items-center gap-2 px-6 py-2.5 rounded-xl
          bg-white/90 text-[#0f172a] text-sm font-semibold
          backdrop-blur-sm
          shadow-[0_0_24px_rgba(255,255,255,0.15)]
          hover:bg-white hover:shadow-[0_0_40px_rgba(255,255,255,0.25)]
          hover:-translate-y-0.5
          transition-[opacity,transform] duration-300
        ">
          {buttonText}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </button>
      </div>

      {/* CSS keyframe for gradient animation */}
      <style>{`
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
      `}</style>
    </div>
  )
}
